import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import PhoneInput from "./PhoneInput";
import { isValidE164Phone } from "../utils/phone-utils";
import { updateMyProfile } from "../services/auth/profile-api";

const isProfileComplete = (profile) => {
  if (!profile) return true;
  const firstName = profile.firstName?.trim() ?? "";
  const lastName = profile.lastName?.trim() ?? "";
  const phone = profile.phone?.trim() ?? "";
  return firstName.length > 1 && lastName.length > 1 && phone.length > 0;
};

export { isProfileComplete };

export default function ProfileCompleteDialog({ open, onClose, profile }) {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [phoneE164, setPhoneE164] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open && profile) {
      setFirstName(profile.firstName?.trim() ?? "");
      setLastName(profile.lastName?.trim() ?? "");
      setPhoneE164(profile.phone?.trim() ?? "");
      setError("");
    }
  }, [open, profile]);

  const firstNameIncomplete = !profile?.firstName || profile.firstName.trim().length <= 1;
  const lastNameIncomplete = !profile?.lastName || profile.lastName.trim().length <= 1;

  const handleSave = async () => {
    if (firstNameIncomplete && (!firstName.trim() || firstName.trim().length < 2)) {
      setError("First name needs to be at least 2 characters.");
      return;
    }
    if (lastNameIncomplete && (!lastName.trim() || lastName.trim().length < 2)) {
      setError("Last name needs to be at least 2 characters.");
      return;
    }
    if (!isValidE164Phone(phoneE164)) {
      setError("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await updateMyProfile({
        ...(firstNameIncomplete && { firstName: firstName.trim() }),
        ...(lastNameIncomplete && { lastName: lastName.trim() }),
        phone: phoneE164,
      });
      onClose({ updated: true });
    } catch (err) {
      setError(err?.message || "Something went wrong saving your profile. Try again?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      maxWidth="xs"
      fullWidth
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.75)" } } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        One quick thing before you hit the ice
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Google filled in what it could — just a few more details and you're all set.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {firstNameIncomplete && (
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
              autoFocus
            />
          )}

          {lastNameIncomplete && (
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
            />
          )}

          <PhoneInput
            value={phoneE164}
            onChange={setPhoneE164}
            disabled={loading}
            size="small"
            helperText="Used by league organizers to reach you if needed"
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={loading}
          sx={{ mt: 3 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Looks good, let's go!"}
        </Button>

      </DialogContent>
    </Dialog>
  );
}
